import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { LOAD_MORE_FEATURED_PROJECTS_ACTION } from '~/routes/actions.load-more-featured-projects';
import { FEATURED_PROJECTS_PAGE_SIZE } from '~/modules/product/util/featured-projects-config';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    CONTENT_CAP,
    DetailTileMedia,
    detailCta,
    detailTileActionsFor,
} from '~/ui-rocco/pages/detail-shared';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { getFeaturedInsideParts } from './featured-inside-statement';

/** Lerp factor per frame — higher snaps tighter, lower trails further. */
const CURSOR_EASE = 0.2;

/**
 * F11 — Tagged in these Projects, per Figma 1841:10347 / 1831:7891 /
 * 1841:10457.
 *
 * A white editorial statement — the product title in small type over a giant
 * "Featured inside {first project} + N others" line — acting as one big
 * button. Hovering it swaps the cursor for a dark "View Projects" bubble that
 * follows the pointer; clicking expands a three-column grid of the projects
 * that feature this product (image, title, professional). The loader SSRs the
 * first page; "Load More" appends later pages via the
 * load-more-featured-projects action. Hidden when no tagged project has a
 * banner image.
 */
export const TaggedProjectsSection = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
    } = useLoaderData<typeof loader>();

    /** A mapped project tile (SSR page 0 and the action return this shape). */
    type Tile = (typeof FeaturedProjects.edges)[number]['node'];

    const [expanded, setExpanded] = useState(false);

    // The "View Projects" cursor bubble eases toward the pointer on a rAF
    // loop, writing transforms straight to the DOM — no React re-renders per
    // mousemove. `bubbleVisible` only toggles the scale/opacity transition.
    const [bubbleVisible, setBubbleVisible] = useState(false);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    // The trigger text — the bubble only reveals (scales up) while the pointer is
    // over these, not anywhere in the section's enlarged hover area.
    const eyebrowRef = useRef<HTMLSpanElement | null>(null);
    const statementRef = useRef<HTMLSpanElement | null>(null);
    const targetRef = useRef({ x: 0, y: 0 });
    const positionRef = useRef({ x: 0, y: 0 });
    const rafRef = useRef<number | null>(null);
    // Defers stopping the easing loop until the fade-out finishes, so the bubble
    // glides to a stop instead of freezing mid-lag at the section edge.
    const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prefetchedRef = useRef(false);

    /** Matches the bubble's transform/opacity transition (duration-300). */
    const BUBBLE_FADE_MS = 300;
    const clearFadeTimeout = () => {
        if (fadeTimeoutRef.current !== null) {
            clearTimeout(fadeTimeoutRef.current);
            fadeTimeoutRef.current = null;
        }
    };
    const stopBubbleLoop = () => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };
    /** True only when the pointer is over the trigger's text, not the padding. */
    const isOverTriggerText = (x: number, y: number) => {
        const within = (el: HTMLElement | null) => {
            const r = el?.getBoundingClientRect();
            return (
                !!r &&
                x >= r.left &&
                x <= r.right &&
                y >= r.top &&
                y <= r.bottom
            );
        };
        return within(eyebrowRef.current) || within(statementRef.current);
    };

    // Pagination: page 0 is SSR'd; "Load More" pages are fetched + appended.
    const fetcher = useFetcher<{
        projects: Tile[];
        hasMore: boolean;
        totalCount: number;
    }>();
    const [appended, setAppended] = useState<Tile[]>([]);
    const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);
    const nextOffsetRef = useRef(FEATURED_PROJECTS_PAGE_SIZE);
    const requestedOffsetRef = useRef(-1);
    const processedOffsetRef = useRef(-1);

    const stepBubble = () => {
        const position = positionRef.current;
        const target = targetRef.current;
        position.x += (target.x - position.x) * CURSOR_EASE;
        position.y += (target.y - position.y) * CURSOR_EASE;
        if (bubbleRef.current) {
            bubbleRef.current.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
        }
        rafRef.current = requestAnimationFrame(stepBubble);
    };

    const handleSectionMouseMove = (event: React.MouseEvent) => {
        // Collapsed only — once expanded the project links use the default cursor.
        if (expanded) return;
        // Back in the section before a pending fade finished — keep the loop.
        clearFadeTimeout();
        targetRef.current = { x: event.clientX, y: event.clientY };
        if (rafRef.current === null) {
            // First move after entering the section: snap, then start easing.
            positionRef.current = { x: event.clientX, y: event.clientY };
            rafRef.current = requestAnimationFrame(stepBubble);
        }
        // Position keeps following across the section, but the bubble only
        // reveals over the trigger text (React bails when the value is unchanged).
        setBubbleVisible(isOverTriggerText(event.clientX, event.clientY));
    };

    const handleSectionMouseLeave = () => {
        // Fade out now, but keep easing toward the last pointer position for the
        // fade's duration so the bubble glides to a stop instead of freezing
        // mid-lag at the section edge; stop the loop once it's invisible.
        setBubbleVisible(false);
        clearFadeTimeout();
        fadeTimeoutRef.current = setTimeout(stopBubbleLoop, BUBBLE_FADE_MS);
    };

    useEffect(
        () => () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            if (fadeTimeoutRef.current !== null)
                clearTimeout(fadeTimeoutRef.current);
        },
        [],
    );

    const resetPagination = useCallback(() => {
        setAppended([]);
        setServerHasMore(null);
        nextOffsetRef.current = FEATURED_PROJECTS_PAGE_SIZE;
        requestedOffsetRef.current = -1;
        processedOffsetRef.current = -1;
    }, []);

    // Navigating to another product reuses this component instance (same
    // route), so React state survives. Collapse and clear pagination on a
    // product change so the previous product's appended projects can't bleed
    // into the new product's grid, and re-arm the hover prefetch.
    useEffect(() => {
        setExpanded(false);
        resetPagination();
        prefetchedRef.current = false;
    }, [product.Link, resetPagination]);

    // Expanding reverts to the default cursor: stop the easing loop and fade
    // the bubble out. Collapsing resets pagination back to the first page.
    useEffect(() => {
        if (!expanded) {
            resetPagination();
            return;
        }
        setBubbleVisible(false);
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (fadeTimeoutRef.current !== null)
            clearTimeout(fadeTimeoutRef.current);
    }, [expanded, resetPagination]);

    const initialTiles = useMemo(
        () => FeaturedProjects.edges.map((edge) => edge.node),
        [FeaturedProjects],
    );

    const fetchPage = useCallback(
        (offset: number) => {
            if (requestedOffsetRef.current === offset) return;
            requestedOffsetRef.current = offset;
            const params = new URLSearchParams({
                link: product.Link,
                offset: String(offset),
                limit: String(FEATURED_PROJECTS_PAGE_SIZE),
            });
            fetcher.load(
                `${LOAD_MORE_FEATURED_PROJECTS_ACTION}?${params.toString()}`,
            );
        },
        [fetcher, product.Link],
    );

    // Append each "Load More" response once, skipping anything already loaded.
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (processedOffsetRef.current === requestedOffsetRef.current) return;

        const incoming = fetcher.data.projects ?? [];
        // A real page always carries at least one project (the button only
        // shows while hasNextPage is true), so an empty payload means the
        // action rejected the request or hit a 500. Don't advance the offset or
        // hide the control — clear the in-flight guard so the same offset can
        // be retried on the next click.
        if (incoming.length === 0) {
            requestedOffsetRef.current = -1;
            return;
        }

        processedOffsetRef.current = requestedOffsetRef.current;
        setAppended((prev) => {
            const seen = new Set(
                [...initialTiles, ...prev].map((tile) => tile.id),
            );
            const fresh = incoming.filter((tile) => !seen.has(tile.id));
            return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerHasMore(Boolean(fetcher.data.hasMore));
        nextOffsetRef.current =
            requestedOffsetRef.current + FEATURED_PROJECTS_PAGE_SIZE;
    }, [fetcher.state, fetcher.data, initialTiles]);

    const loadMore = useCallback(
        () => fetchPage(nextOffsetRef.current),
        [fetchPage],
    );

    const tiles = useMemo(
        () =>
            [...initialTiles, ...appended].filter(
                (tile) => tile.images?.[0]?.src,
            ),
        [initialTiles, appended],
    );

    const firstTile = tiles[0];
    if (!firstTile) return null;

    // The connection's totalCount is the real total even though only a page is
    // loaded, so the "+ N others" count stays accurate.
    const { projectLabel, othersSuffix } = getFeaturedInsideParts(
        firstTile.title,
        FeaturedProjects.pageInfo.totalCount - 1,
    );
    const statement = `Featured inside ${projectLabel}${othersSuffix}`;

    const hasMore = serverHasMore ?? FeaturedProjects.pageInfo.hasNextPage;
    const isLoadingMore = fetcher.state !== 'idle';

    // The grid only mounts on expand, so its images can't start loading until
    // then — hence the visible pop-in. Warm the first page on hover intent (at
    // low priority, once) so they're cached by the time the section expands.
    const prefetchTiles = () => {
        if (prefetchedRef.current) return;
        prefetchedRef.current = true;
        for (const tile of tiles.slice(0, FEATURED_PROJECTS_PAGE_SIZE)) {
            const src = tile.images[0]?.src;
            if (!src) continue;
            const image = new Image();
            image.fetchPriority = 'low';
            image.src = src;
        }
    };

    return (
        <section
            onMouseEnter={prefetchTiles}
            onMouseMove={handleSectionMouseMove}
            onMouseLeave={handleSectionMouseLeave}
            className={cn(
                'w-full bg-white text-[#181818]',
                // Enlarge the hover/cursor target into the neighbouring spacers
                // (desktop) without shifting content: each padding is cancelled
                // by an equal negative margin, so spacing is visually unchanged.
                // Both stay within the 5rem (80px) gap to the accordion, so the
                // padded box never overshoots into it. `relative` lets the padded
                // box paint above the spacers it overlaps so the whole area
                // captures the pointer (a static trailing spacer would otherwise
                // steal the hover over the bottom padding); it does not create a
                // containing block for the fixed bubble.
                'md:relative md:-mt-[5rem] md:-mb-[3rem] md:pt-[5rem] md:pb-[3rem]',
                // Custom "View Projects" cursor only while collapsed; expanding
                // hands the project links back the default pointer.
                !expanded &&
                    'md:cursor-none md:[&_a]:cursor-none md:[&_button]:cursor-none',
            )}
        >
            <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                aria-expanded={expanded}
                className={cn(
                    CONTENT_CAP,
                    'block text-left max-md:px-4 md:px-[3rem]',
                )}
            >
                <span
                    ref={eyebrowRef}
                    className="block w-fit font-sans leading-[1.22] font-medium tracking-[-0.05em] max-md:text-sm md:text-[0.9375rem]"
                >
                    {product.Title}
                </span>
                {/* max-width forces the 2-line wrap (Figma 90.9rem box); balance
                    evens the two lines so the trailing "+ N others" never orphans. */}
                <span
                    ref={statementRef}
                    className="mt-[1.7rem] block w-fit max-w-[90.9rem] font-twk-ghost leading-[0.94] font-normal tracking-[-0.02em] text-balance max-md:mt-3 max-md:text-[2.25rem] md:text-[4.8635rem]"
                >
                    {statement}
                </span>
            </button>

            {/* "View Projects" cursor bubble — eases after the pointer within
                the section (desktop only; the section has no transformed
                ancestor, so fixed positioning tracks the viewport). The outer
                node is driven by the rAF loop; the inner handles the
                scale/fade transition. */}
            <div
                ref={bubbleRef}
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-50 max-md:hidden"
            >
                <div
                    className={cn(
                        // Tailwind v4 maps scale-*/-translate-* to the native
                        // `scale`/`translate` properties (not `transform`), so the
                        // transition must target `scale` for the zoom to animate.
                        'flex size-[8.875rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#181818]/90 transition-[scale,opacity] duration-300 ease-out',
                        bubbleVisible
                            ? 'scale-100 opacity-100'
                            : 'scale-50 opacity-0',
                    )}
                >
                    <span className="font-sans leading-[1.3] font-medium tracking-[0.02em] text-[#f3f3f3] md:text-[0.916rem]">
                        View Projects
                    </span>
                </div>
            </div>

            {expanded ? (
                <div
                    className={cn(
                        CONTENT_CAP,
                        'max-md:px-4 max-md:pt-8 md:px-[3rem] md:pt-[5rem]',
                    )}
                >
                    <div className="grid gap-x-[1.375rem] gap-y-[6.25rem] max-md:grid-cols-2 max-md:gap-x-3.5 max-md:gap-y-8 md:grid-cols-3">
                        {tiles.map((tile) => {
                            const actions = detailTileActionsFor({
                                id: tile.id,
                                link: tile.link,
                                title: tile.title,
                                images: tile.images,
                                professional: tile.professional,
                                professionalLink: tile.professional?.link,
                                pinType: 'ProjectPin',
                            });
                            return (
                                <div key={tile.id} className="relative">
                                    <RoccoLink
                                        to={tile.link}
                                        className="block w-full"
                                    >
                                        <DetailTileMedia
                                            src={tile.images[0]?.src}
                                            alt={
                                                tile.images[0]?.alt ??
                                                tile.title
                                            }
                                            className="bg-neutral-200 aspect-[592/430]"
                                            imgClassName="max-w-none"
                                            pinId={tile.id}
                                            pinType="ProjectPin"
                                            enableActionPanel
                                            enquiry={actions.enquiry}
                                            share={actions.share}
                                            tileInfo={actions.tileInfo}
                                        />
                                        <p className="m-0 truncate font-sans leading-[1.3] font-medium text-[#161616] max-md:pt-2 max-md:text-sm md:pt-[0.625rem] md:text-[1.375rem]">
                                            {tile.title}
                                        </p>
                                        {tile.professional?.name ? (
                                            <p className="m-0 truncate font-sans leading-[1.3] font-normal text-[#161616] opacity-40 max-md:text-sm md:text-[1.375rem]">
                                                {tile.professional.name}
                                            </p>
                                        ) : null}
                                    </RoccoLink>
                                    {/* Mobile actions live outside the tile
                                        link so a tap on the `•••` can never
                                        navigate. */}
                                    <TileMobileActionsSlot
                                        pinId={tile.id}
                                        pinType="ProjectPin"
                                        enableActionPanel
                                        enquiry={actions.enquiry}
                                        share={actions.share}
                                        tileInfo={actions.tileInfo}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {hasMore ? (
                        <div className="mt-[3rem] flex justify-center max-md:mt-10">
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={isLoadingMore}
                                className={detailCta(
                                    'filled-light',
                                    'disabled:opacity-50 max-md:h-[3.125rem] max-md:w-full md:min-w-[14.4rem]',
                                )}
                            >
                                {isLoadingMore ? 'Loading…' : 'Load More'}
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
};
